import { supabase } from './supabaseClient';
import { Alert, HazardType, LatLng } from '../types';
import tomtomService from './tomtomService';
import aiAgentService, { DisasterResume, RawRSSItem, RawTomTomIncident, RawUSGSEarthquake, RawWeatherAlert } from './aiAgentService';
import { getWeather } from './weatherService';

// USGS Earthquake APIs
const USGS_4POINT5_WEEK = "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/4.5_week.geojson";
const USGS_SIGNIFICANT = "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/significant_week.geojson";

// RSS Feeds
const RSS_FEEDS = [
    "http://feeds.bbci.co.uk/news/world/rss.xml", // Global News
    "https://www.gdacs.org/xml/rss.xml", // GDACS Disasters
];

export interface VerifiedHazard {
    id: string;
    type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    description: string;
    location_lat: number;
    location_lng: number;
    location_name: string;
    source: string;
    source_url: string;
    fact_score: number; // 0-100
    ai_analysis: string;
    magnitude?: number;
    depth?: number;
    affected_area?: string;
    advice: string[];
    is_active: boolean;
    verified_at: string;
    created_at: string;
    expires_at: string;
}

// =====================
// MAIN SYNC FUNCTION
// =====================

/**
 * Fetch global hazards from APIs, verify with AI, and sync to Supabase
 * This should be called on app boot and periodically (every 15 mins)
 */
export async function syncGlobalHazards(userLocation?: LatLng): Promise<{ added: number; updated: number; errors: string[] }> {
    const result = { added: 0, updated: 0, errors: [] as string[] };
    const oneHourAgo = new Date(Date.now() - 3600000).toISOString();



    try {
        // 1. Fetch Earthquakes (USGS)
        const earthquakes = await fetchUSGSEarthquakes();


        // 2. Fetch Traffic (TomTom) - If user location is known
        let trafficIncidents: Alert[] = [];
        if (userLocation) {
            try {
                trafficIncidents = await tomtomService.fetchTomTomIncidents(userLocation, 50); // 50km radius

            } catch (e) {
                console.warn('Failed to fetch TomTom incidents', e);
            }
        }

        // 3. Fetch RSS Feeds
        const rssItems = await fetchRSSFeeds();


        // 4. Fetch Weather (if location provided)
        let weatherAlert: RawWeatherAlert | null = null;
        if (userLocation) {
            try {
                const weather = await getWeather(userLocation, 'en');
                // Check if severe enough to alert
                // Check if severe enough to alert (or user requested weather visibility)
                const isSevere = weather.windSpeed > 40 ||
                    weather.condition.toLowerCase().includes('storm') ||
                    weather.condition.toLowerCase().includes('thunder') ||
                    weather.condition.toLowerCase().includes('rain') || // Explicitly include Rain
                    weather.condition.toLowerCase().includes('snow') || // Explicitly include Snow
                    weather.condition.toLowerCase().includes('fog') ||  // Explicitly include Fog
                    weather.temp > 35 || weather.temp < 0;

                if (isSevere) {
                    weatherAlert = {
                        event: `Severe Weather: ${weather.condition}`,
                        sender_name: 'Digital Guardian Weather',
                        description: `Current conditions: ${weather.condition}, ${weather.temp}°C, Winds ${weather.windSpeed}km/h.`,
                        temp: weather.temp,
                        condition: weather.condition,
                        windBias: weather.windSpeed,
                        isSevere: true
                    };

                }
            } catch (e) {
                console.warn('Weather fetch failed', e);
            }
        }


        // 5. PROCESS VIA AI AGENT
        // We iterate and send to AI agent (which handles Supabase saving)

        // Process Earthquakes
        for (const eq of earthquakes) {
            try {
                // only process recent ones
                if (eq.timestamp < oneHourAgo) continue;

                const raw: RawUSGSEarthquake = {
                    id: eq.id,
                    properties: {
                        mag: eq.magnitude,
                        place: eq.location_name,
                        time: new Date(eq.timestamp).getTime(),
                        sig: eq.severity === 'critical' ? 1000 : 500,
                        title: eq.description,
                        tsunami: eq.tsunami ? 1 : 0,
                        felt: eq.felt,
                        alert: eq.severity
                    },
                    geometry: {
                        coordinates: [eq.location_lng, eq.location_lat, eq.depth]
                    }
                };

                // AI Agent generates resume and saves to Supabase
                await aiAgentService.generateDisasterResumeWithAI(raw, 'usgs');
                result.added++;
            } catch (e: any) {
                result.errors.push(`EQ Error ${eq.id}: ${e.message}`);
            }
        }

        // Process Traffic
        for (const inc of trafficIncidents) {
            try {
                const raw: RawTomTomIncident = {
                    id: inc.id,
                    iconCategory: 1, // Simplified
                    magnitudeOfDelay: inc.severity === 'critical' ? 4 : (inc.severity === 'high' ? 3 : 2),
                    description: inc.description,
                    point: { latitude: inc.coordinates.lat, longitude: inc.coordinates.lng },
                    from: inc.location,
                    delay: 300 // default
                };
                await aiAgentService.generateDisasterResumeWithAI(raw, 'tomtom');
                result.added++;
            } catch (e) { }
        }

        // Process RSS
        for (const item of rssItems) {
            try {
                await aiAgentService.generateDisasterResumeWithAI(item, 'rss', userLocation);
                result.added++;
            } catch (e) { }
        }

        // Process Weather Impact
        if (weatherAlert && userLocation) {
            try {
                await aiAgentService.generateDisasterResumeWithAI(weatherAlert, 'weather', userLocation);
                result.added++;
            } catch (e) { }
        }



    } catch (e: any) {
        result.errors.push(`Sync failed: ${e.message}`);
        console.error('❌ Hazard sync failed:', e);
    }

    return result;
}

// =====================
// USGS EARTHQUAKE FETCH
// =====================

interface RawEarthquake {
    id: string;
    magnitude: number;
    depth: number;
    location_lat: number;
    location_lng: number;
    location_name: string;
    timestamp: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    description: string;
    source_url: string;
    tsunami: boolean;
    felt: number;
    advice: string[];
}

async function fetchUSGSEarthquakes(): Promise<RawEarthquake[]> {
    const earthquakes: RawEarthquake[] = [];

    try {
        const [responseAll, responseSig] = await Promise.all([
            fetch(USGS_4POINT5_WEEK).catch(() => null),
            fetch(USGS_SIGNIFICANT).catch(() => null)
        ]);

        const seen = new Set<string>();

        if (responseSig?.ok) {
            const data = await responseSig.json();
            for (const f of data.features || []) {
                const eq = parseUSGSFeature(f, true);
                if (eq && !seen.has(eq.id)) {
                    seen.add(eq.id);
                    earthquakes.push(eq);
                }
            }
        }

        if (responseAll?.ok) {
            const data = await responseAll.json();
            for (const f of data.features || []) {
                const eq = parseUSGSFeature(f, false);
                if (eq && !seen.has(eq.id)) {
                    seen.add(eq.id);
                    earthquakes.push(eq);
                }
            }
        }
    } catch (e) {
        console.error('USGS fetch error:', e);
    }
    return earthquakes;
}

function parseUSGSFeature(feature: any, isSignificant: boolean): RawEarthquake | null {
    try {
        const [lng, lat, depth] = feature.geometry.coordinates;
        const props = feature.properties;
        const mag = props.mag;
        const place = props.place || 'Unknown Location';
        const time = new Date(props.time);
        const tsunami = props.tsunami > 0;
        const felt = props.felt || 0;

        let severity: 'low' | 'medium' | 'high' | 'critical' = 'medium';
        if (mag >= 7 || isSignificant || tsunami) severity = 'critical';
        else if (mag >= 6) severity = 'high';
        else if (mag >= 5) severity = 'medium';
        else severity = 'low';

        let desc = `Magnitude ${mag.toFixed(1)} earthquake at ${depth.toFixed(0)}km depth.`;
        if (tsunami) desc += ' ⚠️ TSUNAMI WARNING ISSUED.';
        if (felt > 100) desc += ` Felt by ${felt} people.`;

        return {
            id: feature.id,
            magnitude: mag,
            depth,
            location_lat: lat,
            location_lng: lng,
            location_name: place,
            timestamp: time.toISOString(),
            severity,
            description: desc,
            source_url: props.url || '',
            tsunami,
            felt,
            advice: [] // Simplified advice as it's handled by AI Agent now
        };
    } catch (e) {
        return null;
    }
}

// =====================
// RSS FETCH
// =====================

async function fetchRSSFeeds(): Promise<RawRSSItem[]> {
    const items: RawRSSItem[] = [];

    // ATTEMPT TO FETCH REAL RSS (via CORS Proxy)
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent("https://www.gdacs.org/xml/rss.xml")}`, {
            signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (response.ok) {
            const data = await response.json();
            // Minimal parsing if needed, but for now we just return empty to stop the spam 
            // because parsing RSS text is complex here without a library like 'rss-parser'.
            // We will rely on USGS/TomTom for real hazards.
        }
    } catch (e) {
        console.warn("RSS Fetch failed", e);
    }

    return items;
}

// =====================
// FETCH FROM SUPABASE
// =====================

/**
 * Get all verified active hazards from Supabase for map display
 */
export async function getVerifiedHazards(): Promise<Alert[]> {
    try {
        const { data, error } = await supabase
            .from('hazards')
            .select('*')
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(100);

        if (error || !data) {
            console.error('Failed to fetch hazards from Supabase:', error);
            return [];
        }

        return data.map(h => ({
            id: h.id,
            type: (h.type as HazardType) || HazardType.WEATHER,
            severity: h.severity as any,
            location: h.location || 'Unknown Sector',
            coordinates: {
                lat: h.metadata?.lat || 0, // Prefer metadata lat/lng from AI Agent
                lng: h.metadata?.lng || 0
            },
            timestamp: h.created_at,
            time: getTimeAgo(new Date(h.created_at)),
            distance: '0km',
            isOfficial: true,
            description: h.description,
            advice: h.metadata?.action_step ? [h.metadata.action_step] : [],
            source: h.source,
            truthScore: 90,
            imageUrl: h.metadata?.imageUrl // if available
        }));

    } catch (e) {
        console.error('Error fetching verified hazards:', e);
        return [];
    }
}

/**
 * Subscribe to real-time hazard updates
 */
export function subscribeToVerifiedHazards(callback: (hazard: Alert) => void) {
    return supabase.channel('verified-hazards')
        .on('postgres_changes',
            { event: 'INSERT', schema: 'public', table: 'hazards' },
            (payload) => {
                const h = payload.new as any;
                callback({
                    id: h.id,
                    type: h.type as HazardType,
                    severity: h.severity,
                    location: h.location,
                    coordinates: { lat: h.metadata?.lat || 0, lng: h.metadata?.lng || 0 },
                    timestamp: h.created_at,
                    time: 'Just now',
                    distance: '0km',
                    isOfficial: true,
                    description: h.description,
                    source: h.source,
                    truthScore: 90,
                    advice: h.metadata?.action_step ? [h.metadata.action_step] : []
                });
            }
        )
        .subscribe();
}

function getTimeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    return diffMins < 60 ? `${diffMins}m ago` : `${Math.floor(diffMins / 60)}h ago`;
}
